import React from "react";
import "../Common/css/styles.css";
import "../Common/css/user.css";
import { Button, Dialog, Divider, FormLabel, Grid, IconButton, Menu, MenuItem, TextField } from "@mui/material";
import { PlusOutlined } from "@ant-design/icons";
import CustomAlert from "src/Components/Alerts";
import Spinner from "src/Components/Spinner";
import { createInstitutionUpdate, deleteInstitutionUpdate, getInstitutionUpdates } from "src/Services/admin_api";
import TableToolbar from "src/Components/TableToolbar";
import { MoreVert } from "@mui/icons-material";
import Dialogbox from "src/Components/Dialogbox";

export default function AdminInstitutionUpdate() {
    const [title, setTitle] = React.useState<string>("");
    const [description, setDescription] = React.useState<string>("");
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const [alertOpen, setAlertOpen] = React.useState<boolean>(false);
    const [alertDescription, setAlertDescription] = React.useState<string>("");
    const [alertType, setAlertType] = React.useState<"success" | "error" | "warning">("success");
    const [institutionUpdates, setInstitutionUpdates] = React.useState<any[]>([]);
    const [searchTerm, setSearchTerm] = React.useState<string>("");
    const [openModal, setOpenModal] = React.useState<boolean>(false);
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const [selectedUpdate, setSelectedUpdate] = React.useState<any>(null);
    const [viewDialogOpen, setViewDialogOpen] = React.useState<boolean>(false);
    const [isOpen, setIsOpen] = React.useState<boolean>(false);

    const showAlert = (description: string, type: "success" | "error" | "warning") => {
        setAlertDescription(description);
        setAlertType(type);
        setAlertOpen(true);
    };

    const token = sessionStorage.getItem("token") || "";
    const userId = sessionStorage.getItem("adminId") || "";

    const handleAdd = async () => {
        setIsLoading(true);
        const body = {
            userId,
            title,
            message: description,
            userType: 3
        }

        try {
            await createInstitutionUpdate(token, body);
            setIsLoading(false);
            showAlert("Institution update added successfully", "success");
            setTitle("");
            setDescription("");
            handleGetInstitutionUpdates();
        } catch (error) {
            setIsLoading(false);
            showAlert("Failed to add institution update", "error");
        }
    }

    const handleGetInstitutionUpdates = async () => {
        try {
            const res = await getInstitutionUpdates(token);
            setInstitutionUpdates(res);
        } catch (error) {
            setInstitutionUpdates([]);
        }
    }

    React.useEffect(() => {
        handleGetInstitutionUpdates();
    }, []);

    const filteredInstitutionUpdates = institutionUpdates.filter((update) => {
        const term = searchTerm.toLowerCase();

        return (
            update.noticeTitle?.toLowerCase().includes(term) ||
            update.noticeMessage?.toLowerCase().includes(term)
        );
    });

    const handleMenuClick = (event: React.MouseEvent<HTMLElement>, update: any) => {
        setAnchorEl(event.currentTarget);
        setSelectedUpdate(update);
    }

    const handleMenuClose = () => {
        setAnchorEl(null);
        setSelectedUpdate(null);
    };

    const handleDelete = async (id: string) => {
        try {
            await deleteInstitutionUpdate(token, id, userId, "3");
            showAlert("Institution update deleted successfully", "success");
            handleGetInstitutionUpdates();
            handleMenuClose();
        } catch (error) {
            showAlert("Failed to delete institution update", "error");
            handleMenuClose();
        }
    }

    return (
        <div className="comman-outer">
            <Dialogbox
                open={isOpen}
                title="Delete Record"
                content="Are you sure you want to delete this record?"
                agreeButtonText="Delete"
                disagreeButtonText="Cancel"
                onAgree={() => handleDelete(selectedUpdate.id)}
                onDisagree={() => setIsOpen(false)}
                onClose={() => setIsOpen(false)}
            />
            <CustomAlert
                open={alertOpen}
                onClose={() => setAlertOpen(false)}
                description={alertDescription}
                type={alertType}
                timeout={3000}
            />
            <Spinner isLoading={isLoading} />
            <div>
                <div className="table-title-outer">
                    <div className="table-title">Add New Institution Update</div>
                </div>
                <div className="space-bar"></div>
                <div className="card-outer">
                    <Grid container spacing={2} style={{ padding: "16px" }} className="form-grid ">
                        <Grid item xs={12}>
                            <FormLabel component="legend" sx={{ color: "#3d3d3dff", mb: 1 }}>Title</FormLabel>
                            <TextField
                                fullWidth
                                variant="outlined"
                                placeholder="Title"
                                size="small"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                sx={{
                                    backgroundColor: "black",
                                    input: {
                                        color: "#ffffff",
                                        "::placeholder": {
                                            color: "grey",
                                            opacity: 1,
                                        },
                                    },
                                    "& .MuiOutlinedInput-root": {
                                        "& fieldset": {
                                            borderColor: "#555",
                                        },
                                        "&:hover fieldset": {
                                            borderColor: "#aaa",
                                        },
                                        "&.Mui-focused fieldset": {
                                            borderColor: "gray",
                                        },
                                    },
                                }}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <FormLabel component="legend" sx={{ color: "#3d3d3dff", mb: 1 }}>Description</FormLabel>
                            <TextField
                                fullWidth
                                variant="outlined"
                                placeholder="Description"
                                size="small"
                                multiline
                                rows={4}
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                sx={{
                                    backgroundColor: "black",
                                    "& textarea": {
                                        color: "#ffffff",                // text color
                                    },
                                    "& textarea::placeholder": {
                                        color: "grey",                   // placeholder
                                        opacity: 1,
                                    },
                                    "& .MuiOutlinedInput-root": {
                                        "& fieldset": {
                                            borderColor: "#555",
                                        },
                                        "&:hover fieldset": {
                                            borderColor: "#aaa",
                                        },
                                        "&.Mui-focused fieldset": {
                                            borderColor: "gray",
                                        },
                                    },
                                }}
                            />
                        </Grid>
                    </Grid>
                </div>
                <div className="pagination-footer">
                    <div></div>

                    <Button
                        className="primary-btn"
                        variant="contained"
                        size="small"
                        onClick={handleAdd}
                        startIcon={<PlusOutlined style={{ fontSize: "14px", color: "black" }} />}
                        disabled={!title || !description}
                    >
                        Add Institution Update
                    </Button>
                </div>
            </div>
            <Divider style={{ margin: "24px 0", borderColor: "#444" }} />

            <div>
                <TableToolbar
                    title="Institution Updates"
                    searchTerm={searchTerm}
                    setSearchTerm={setSearchTerm}
                    isButton={false}
                    buttonText="Institution Update"
                    onButtonClick={() => setOpenModal(true)}
                    buttonIcon={<PlusOutlined style={{ fontSize: "14px", color: "black" }} />}
                />
                <div className="space-bar"></div>

                {filteredInstitutionUpdates.length > 0 ? filteredInstitutionUpdates.map((update) => (
                    <div key={update.id} className="card-outer" style={{ marginBottom: "16px" }}>
                        <div className="card-outer1">
                            <p className="t-card">Title: {update.noticeTitle}</p>
                            <p className="t-card">Description: {update.noticeMessage.length > 20 ? update.noticeMessage.substring(0, 20) + "..." : update.noticeMessage}</p>
                            <p className="t-card">Created Date: {update.createdAt.split("T")[0]}</p>
                            <p className="t-card">Created Time: {update.createdAt.split("T")[1].split(".")[0]}</p>
                        </div>
                        <IconButton onClick={(e) => handleMenuClick(e, update)}>
                            <MoreVert style={{ color: "white" }} />
                        </IconButton>
                    </div>
                )) : (
                    <div className="no-data">No institution updates found.</div>
                )}

                <div className="pagination-footer"></div>

                <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleMenuClose}
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                    transformOrigin={{ vertical: "top", horizontal: "right" }}
                >
                    <MenuItem onClick={() => setViewDialogOpen(true)}>View</MenuItem>
                    <MenuItem onClick={() => setIsOpen(true)}>Delete</MenuItem>
                </Menu>
            </div>

            <Dialog
                open={viewDialogOpen}
                onClose={() => {
                    setViewDialogOpen(false)
                    handleMenuClose();
                }}
                fullWidth
                maxWidth="sm"
                PaperProps={{
                    sx: { backgroundColor: "#c6c2c2e3", color: "white", paddingBottom: 2, borderRadius: 2, boxShadow: 24, border: "1px solid #ffffffff" },
                }}
            >
                <div style={{ display: "flex", justifyContent: "center", alignItems: "center", borderBottom: "1px solid #555", backgroundColor: "#393939", padding: "25px 16px" }}>
                    <h3>Notice Details</h3>
                </div>

                <Grid container spacing={2} style={{ padding: "25px 16px", color: "white" }} className="form-grid">
                    <Grid item xs={12}>
                        <strong>Title:</strong> {selectedUpdate?.noticeTitle}
                    </Grid>
                    <Grid item xs={12}>
                        <strong>Description:</strong> {selectedUpdate?.noticeMessage}
                    </Grid>
                    <Grid item xs={12}>
                        <strong>Date:</strong> {selectedUpdate?.createdAt.split("T")[0]}
                    </Grid>
                    <Grid item xs={12}>
                        <strong>Time:</strong> {selectedUpdate?.createdAt.split("T")[1].split(".")[0]}
                    </Grid>
                </Grid>
            </Dialog>
        </div>
    );
}